// loadsave.ts ↔ game/loadsave.cpp — the save/load slot pickers
// PickLoadGameStream / PickSaveGameStream the shell (LOAD SAVED GAME), the
// in-game menu (SAVE/LOAD GAME) and the objectives summary (LOAD GAME) run. The
// canvas LoadGameForm slot-list was removed (canvas-removal R1); the DOM
// load/save overlays (dom/ingame/...) enumerate + pick the slot now, fed by the
// domLoadGame/domSaveGame seams, and these drivers open the stream as before.

import type { Stream } from '../../../sim/src/sm/stream.ts';
import type { ShellEnv } from './shellform.ts';
import { HtMessageBox, kfMbWhiteBorder } from './msgbox.ts';
import { HostOpenSaveGameStream, HostNewSaveGameStream, type SaveStore } from '../io/savegame.ts';

// game/res.h:35-36, 262
export const kidfLoadGame = 1038;
export const kidfSaveGame = 1039;
export const kidcGameList = 1003;

/** The C++ globals this slice reaches (gpmfrmm/gpiniForms/gtimm) + the
 *  HostOpenSaveGameStream storage seam + the ?dom load/save overlay hooks. */
export type LoadSaveEnv = Pick<
  ShellEnv,
  'mfrmm' | 'gtimm' | 'domLoadGame' | 'domSaveGame' | 'domMessageBox'
>;

// game/loadsave.cpp:5-29 — PickLoadGameStream; done(pstm|null) is the C++
// return (null = canceled).
export function PickLoadGameStream(
  env: LoadSaveEnv,
  store: SaveStore,
  done: (pstm: Stream | null) => void,
): void {
  // ?dom: the DOM load overlay (env.domLoadGame). The HOST enumerates the slots (saves-io)
  // and reports the picked slot; we open the stream exactly as the canvas does, re-showing
  // with a message box on an empty/invalid slot (the canvas re-show loop, loadsave.cpp).
  if (env.domLoadGame !== undefined) {
    const ask = (): void =>
      env.domLoadGame!(store, (slot) => {
        if (slot === null) {
          done(null);
          return;
        }
        const pstm = HostOpenSaveGameStream(store, slot);
        if (pstm !== null) {
          done(pstm);
          return;
        }
        HtMessageBox(
          env as ShellEnv,
          kfMbWhiteBorder,
          'Load Saved Game',
          'Please select a valid saved game to load first!',
          () => ask(),
        );
      });
    ask();
    return;
  }

  // No DOM load overlay seam wired (e.g. a headless harness) — nothing to pick.
  done(null);
}

// game/loadsave.cpp:31-45 — PickSaveGameStream; done(pstm|null) is the C++
// (bool, *ppstm) pair (null = canceled). pszTitle = gsim.GetLevel()->GetTitle().
export function PickSaveGameStream(
  env: LoadSaveEnv,
  store: SaveStore,
  getLevelTitle: () => string,
  done: (pstm: Stream | null) => void,
): void {
  // ?dom: the DOM save overlay (env.domSaveGame). The HOST enumerates the slots and reports
  // the chosen slot; we open the new save stream (named by the level title) exactly as the
  // canvas does. Cancel ⇒ done(null) (single-shot, no re-show — matches the canvas).
  if (env.domSaveGame !== undefined) {
    env.domSaveGame(store, (slot) => {
      done(slot === null ? null : HostNewSaveGameStream(store, slot, getLevelTitle()));
    });
    return;
  }

  // No DOM save overlay seam wired (e.g. a headless harness) — nothing to pick.
  done(null);
}
